import React, { useEffect, useRef, useState } from "react";

type StockCategory = "Domestic" | "Kitchen" | "Maintenance";

interface StockItem {
  code: string;
  name: string;
  category: StockCategory;
  qty: number;
  min: number;
  unit: string;
  updated: string;
}

interface StockMove {
  id: string;
  code: string;
  name: string;
  kind: "in" | "out";
  qty: number;
  by: string;
  at: string;
}

const ITEMS_KEY = "stock_items";
const MOVES_KEY = "stock_moves";

function loadItems(): StockItem[] {
  try {
    return JSON.parse(localStorage.getItem(ITEMS_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function loadMoves(): StockMove[] {
  try {
    return JSON.parse(localStorage.getItem(MOVES_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function useBarcodeScanner(onCode: (code: string) => void) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<any>(null);
  const [active, setActive] = useState(false);
  const [supported, setSupported] = useState(true);

  const stop = () => {
    setActive(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const start = async () => {
    const AnyWindow: any = window;

    if (!AnyWindow.BarcodeDetector || !navigator.mediaDevices) {
      setSupported(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });

      streamRef.current = stream;
      setActive(true);

      const detector = new AnyWindow.BarcodeDetector({
        formats: ["ean_13", "ean_8", "code_128", "code_39", "qr_code", "upc_a", "upc_e"],
      });

      timerRef.current = setInterval(async () => {
        const video = videoRef.current;

        if (!video) return;

        try {
          const codes = await detector.detect(video);

          if (codes.length > 0 && codes[0].rawValue) {
            onCode(String(codes[0].rawValue));
            stop();
          }
        } catch (error) {
          return;
        }
      }, 400);
    } catch (error) {
      setSupported(false);
    }
  };

  useEffect(() => {
    if (active && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [active]);

  useEffect(() => stop, []);

  return { videoRef, active, supported, start, stop };
}
export default function Stock({ user }: { user?: any }) {
  const [tab, setTab] = useState<"list" | "add" | "in" | "out" | "history">("list");
  const [items, setItems] = useState<StockItem[]>(loadItems);
  const [moves, setMoves] = useState<StockMove[]>(loadMoves);
  const [message, setMessage] = useState("");

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState<StockCategory>("Domestic");
  const [qty, setQty] = useState("0");
  const [min, setMin] = useState("1");
  const [unit, setUnit] = useState("pcs");

  const scanner = useBarcodeScanner(value => {
    setCode(value);
    setMessage(`Scanned code ${value}`);
  });

  const saveItems = (next: StockItem[]) => {
    setItems(next);
    localStorage.setItem(ITEMS_KEY, JSON.stringify(next));
  };

  const saveMoves = (next: StockMove[]) => {
    setMoves(next);
    localStorage.setItem(MOVES_KEY, JSON.stringify(next));
  };

  const addItem = () => {
    if (!code || !name) {
      setMessage("Code and name are required. Scan or type the code.");
      return;
    }

    const next = items.filter(item => item.code !== code);

    next.push({
      code,
      name,
      category,
      qty: Number(qty) || 0,
      min: Number(min) || 0,
      unit,
      updated: new Date().toISOString(),
    });

    saveItems(next);
    setMessage(`Item ${name} saved to ${category}.`);
    setCode("");
    setName("");
    setQty("0");
  };

  const moveStock = (kind: "in" | "out") => {
    const item = items.find(entry => entry.code === code);

    if (!item) {
      setMessage("No item with that code. Add it first on the Add Product tab.");
      return;
    }

    const amount = Number(qty) || 0;

    if (amount <= 0) {
      setMessage("Enter a quantity.");
      return;
    }

    const newQty = kind === "in" ? item.qty + amount : Math.max(0, item.qty - amount);

    saveItems(items.map(entry => entry.code === code ? { ...entry, qty: newQty, updated: new Date().toISOString() } : entry));

    saveMoves([
      { id: `mv-${Date.now()}`, code, name: item.name, kind, qty: amount, by: user && user.name ? user.name : "Unknown", at: new Date().toISOString() },
      ...moves,
    ]);

    const low = newQty <= item.min ? " LOW STOCK." : "";

    setMessage(`${kind === "in" ? "Received" : "Issued"} ${amount} ${item.unit} of ${item.name}. New level ${newQty} ${item.unit}.${low}`);
    setQty("0");
  };
  const categories: StockCategory[] = ["Domestic", "Kitchen", "Maintenance"];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Stock</h2>
        <p className="text-sm text-slate-600">Domestic, kitchen and maintenance stock with barcode scanning for adding, receiving and issuing.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(["list", "add", "in", "out", "history"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>
            {t === "list" ? "Stock List" : t === "add" ? "Add Product" : t === "in" ? "Receive" : t === "out" ? "Issue / Take Out" : "History"}
          </button>
        ))}
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {scanner.active ? (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 p-6">
          <video ref={scanner.videoRef} autoPlay playsInline className="h-72 w-full max-w-md rounded-xl object-cover" />
          <div className="mt-4 text-sm text-white">Point the camera at the barcode</div>
          <button onClick={scanner.stop} className="mt-4 rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-900">Cancel</button>
        </div>
      ) : null}

      {!scanner.supported ? <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Camera scanning is not supported on this browser. Type the code instead.</div> : null}

      {tab === "list" ? (
        <div className="space-y-4">
          {categories.map(cat => (
            <div key={cat} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-semibold text-slate-900">{cat}</h3>
              <div className="space-y-2">
                {items.filter(item => item.category === cat).map(item => (
                  <div key={item.code} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-2">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{item.name}</div>
                      <div className="text-xs text-slate-500">Code {item.code}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.qty <= item.min ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">LOW</span> : null}
                      <span className="text-sm font-semibold text-slate-900">{item.qty} {item.unit}</span>
                      <span className="text-xs text-slate-500">min {item.min}</span>
                    </div>
                  </div>
                ))}
                {items.filter(item => item.category === cat).length === 0 ? <div className="text-sm text-slate-500">No items yet.</div> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "add" ? (
        <div className="max-w-xl space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <button onClick={scanner.start} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Scan barcode</button>
          <input value={code} onChange={event => setCode(event.target.value)} placeholder="Product code" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <input value={name} onChange={event => setName(event.target.value)} placeholder="Product name" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <select value={category} onChange={event => setCategory(event.target.value as StockCategory)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option>Domestic</option>
            <option>Kitchen</option>
            <option>Maintenance</option>
          </select>
          <div className="flex gap-2">
            <input value={qty} onChange={event => setQty(event.target.value)} placeholder="Qty" type="number" className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input value={min} onChange={event => setMin(event.target.value)} placeholder="Min" type="number" className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input value={unit} onChange={event => setUnit(event.target.value)} placeholder="Unit" className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <button onClick={addItem} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save product</button>
        </div>
      ) : null}

      {tab === "in" || tab === "out" ? (
        <div className="max-w-xl space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <button onClick={scanner.start} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Scan barcode</button>
          <input value={code} onChange={event => setCode(event.target.value)} placeholder="Product code" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <input value={qty} onChange={event => setQty(event.target.value)} placeholder="Quantity" type="number" className="w-32 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <button onClick={() => moveStock(tab === "in" ? "in" : "out")} className={tab === "in" ? "rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white" : "rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white"}>
            {tab === "in" ? "Receive stock" : "Take out stock"}
          </button>
        </div>
      ) : null}

      {tab === "history" ? (
        <div className="space-y-2">
          {moves.map(move => (
            <div key={move.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-sm text-slate-800">{move.kind === "in" ? "Received" : "Issued"} {move.qty} x {move.name}</div>
              <div className="text-xs text-slate-500">{new Date(move.at).toLocaleString()} by {move.by}</div>
            </div>
          ))}
          {moves.length === 0 ? <div className="text-sm text-slate-500">No movements yet.</div> : null}
        </div>
      ) : null}
    </div>
  );
}